/**
 * Where a moderated voice room stands, under the world and the team
 * (world-and-team.tsx) in the private call's window and the voice monitor's:
 * connecting, connected, or why it failed (room-failure.ts).
 *
 * Each window wrote the row out for itself, with a key pair of its own for the
 * same two words and the failure's colour in each sheet. It is written once
 * here, each size as it was drawn: the narrow call window's words alone, each
 * state in its colour; the wide monitor's light beside the words. What it says
 * a screen reader hears as it changes, through a StatusLine beside the words:
 * the row as a region of its own would arrive with its words in it, unheard.
 */

import { useTranslation } from 'react-i18next';
import type { FloatingWindowSize } from '../../components/floating-window';
import { StatusLine } from '../../components/status-line';
import { ROOM_FAILURE_KEY, type RoomLink } from './room-failure';
import styles from './room-status.module.css';

/** The row as each window draws it, by the window's width: a window of a new width is drawn here too, or the build fails. */
const SIZE: Record<FloatingWindowSize, string> = {
  narrow: styles.narrow,
  wide: styles.wide,
};

/** What a room on its way, or in, says: one ended says nothing — its window is closing. */
const STATE_KEY = {
  connecting: 'voiceRoom.connecting',
  connected: 'voiceRoom.connected',
} as const;

/** Each state's colour where the row says something: a failure's is the one colour a failure is said in. */
const TONE = {
  connecting: styles.connecting,
  connected: styles.connected,
  error: styles.failed,
} as const;

type RoomStatusProps = RoomLink & {
  /** The width of the window it sits in. */
  size: FloatingWindowSize;
};

export function RoomStatus({ connectionState, failure, size }: RoomStatusProps) {
  const { t } = useTranslation();
  const connected = connectionState === 'connected';
  const words =
    connectionState === 'disconnected'
      ? ''
      : connectionState === 'error'
        ? t(ROOM_FAILURE_KEY[failure ?? 'other'])
        : t(STATE_KEY[connectionState]);
  return (
    <div className={SIZE[size]}>
      {size === 'wide' && (
        <span className={connected ? `${styles.light} ${styles.lit}` : styles.light} aria-hidden="true">
          {connected ? '🟢' : '🔴'}
        </span>
      )}
      {connectionState !== 'disconnected' && <span className={`${styles.words} ${TONE[connectionState]}`}>{words}</span>}
      <StatusLine>{words}</StatusLine>
    </div>
  );
}
